import { useEffect, useMemo, useState } from 'react';

import { useLingui } from '@lingui/react/macro';
import { Button } from '@repo/videa-components/src/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@repo/videa-components/src/ui/dialog';
import { Input } from '@repo/videa-components/src/ui/input';

import type { DailyGoal } from '../../../../react-query/useDailyGoals';
import {
  formatCurrencyDisplay,
  parseCurrencyInput,
  sanitizeNumericInput,
} from '../../../../utils/currency';
import { mixpanelEvents, useTrackEvent } from '../../../../utils/mixpanel';
import { useLocalizedFormatDate } from '../../../claims/freeClaims/state';

export interface SetDailyGoalModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  date: Date | string;
  goals: DailyGoal[];
  onSave: (updates: { providerId: string; goal: number }[]) => void;
  isLoading?: boolean;
}

const computeGoalFromInput = (
  goal: DailyGoal,
  goalValues: Record<string, string>,
): number => {
  const raw = goalValues[goal.providerId] ?? formatCurrencyDisplay(goal.goal);
  const parsed = parseCurrencyInput(raw);
  const normalizedCurrentGoal = Math.round(goal.goal);
  return !Number.isNaN(parsed) && parsed >= 0 ? parsed : normalizedCurrentGoal;
};

const hasUpdatedAt = (
  goal: DailyGoal,
): goal is DailyGoal & { updatedAt: string } => goal.updatedAt != null;

export const SetDailyGoalModal = ({
  open,
  onOpenChange,
  date,
  goals,
  onSave,
  isLoading = false,
}: SetDailyGoalModalProps) => {
  const { t } = useLingui();
  const { trackEvent } = useTrackEvent();
  const [goalValues, setGoalValues] = useState<Record<string, string>>({});
  const formatDate = useLocalizedFormatDate();

  // "Last updated" from main daily goals table (most recent updatedAt among goals)
  const lastUpdate = useMemo(() => {
    const latest = goals
      .filter(hasUpdatedAt)
      .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))[0];
    if (!latest) return null;
    return {
      date: formatDate(latest.updatedAt, false),
      by: latest.updatedByName ?? latest.updatedBy,
    };
  }, [goals, formatDate]);

  useEffect(() => {
    if (!open) {
      setGoalValues({});
      return;
    }

    setGoalValues((prevGoalValues) => {
      let hasNewValues = false;
      const nextGoalValues = { ...prevGoalValues };

      goals.forEach((goal) => {
        if (nextGoalValues[goal.providerId] === undefined) {
          nextGoalValues[goal.providerId] = formatCurrencyDisplay(goal.goal);
          hasNewValues = true;
        }
      });

      return hasNewValues ? nextGoalValues : prevGoalValues;
    });
  }, [open, goals]);

  const hasChanges = useMemo(
    () =>
      goals.some((goal) => {
        const normalizedCurrentGoal = Math.round(goal.goal);
        const nextGoal = computeGoalFromInput(goal, goalValues);
        return nextGoal !== normalizedCurrentGoal;
      }),
    [goalValues, goals],
  );

  const handleSave = () => {
    const updates = goals.flatMap((goal) => {
      const normalizedCurrentGoal = Math.round(goal.goal);
      const nextGoal = computeGoalFromInput(goal, goalValues);

      if (nextGoal === normalizedCurrentGoal) return [];
      return [{ providerId: goal.providerId, goal: nextGoal }];
    });

    if (updates.length === 0) {
      onOpenChange(false);
      return;
    }

    trackEvent(mixpanelEvents.dailyGoalsSave, {
      context: 'DAILY_GOALS',
      date,
      providerCount: updates.length,
    });
    onSave(updates);
  };

  const handleGoalChange = (providerId: string, value: string) => {
    const sanitized = sanitizeNumericInput(value);
    setGoalValues((prev) => ({ ...prev, [providerId]: sanitized }));
  };

  const handleGoalBlur = (providerId: string) => {
    const raw = goalValues[providerId] ?? '';
    const num = parseCurrencyInput(raw);
    if (!Number.isNaN(num) && num >= 0) {
      setGoalValues((prev) => ({
        ...prev,
        [providerId]: formatCurrencyDisplay(num),
      }));
    }
  };

  const lastUpdatedDate = lastUpdate?.date;
  const lastUpdatedBy = lastUpdate?.by;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="vai-flex vai-max-h-[85vh] vai-w-full vai-max-w-[520px] vai-flex-col vai-gap-4 vai-p-6 vai-overflow-hidden"
        data-testid="set-daily-goal-modal"
      >
        <DialogHeader className="vai-shrink-0 vai-gap-1">
          <DialogTitle className="vai-text-left vai-font-inter vai-text-lg vai-font-semibold vai-leading-6 vai-text-zinc-900">
            {t`Daily Care Goal`}
          </DialogTitle>
          <DialogDescription className="vai-text-left vai-text-sm vai-font-normal vai-leading-5 vai-text-zinc-500">
            {t`Set a daily care goal to track`}
          </DialogDescription>
        </DialogHeader>

        <div
          className={
            goals.length > 5
              ? 'vai-flex vai-max-h-[50vh] vai-min-h-0 vai-flex-col vai-gap-4 vai-overflow-y-scroll vai-overflow-x-hidden vai-mr-[-10px] vai-pr-1'
              : 'vai-flex vai-flex-col vai-gap-4'
          }
        >
          {goals.map((goal, index) => (
            <div
              key={`${goal.providerId}-${goal.date}-${index}`}
              className="vai-flex vai-flex-col vai-gap-1"
            >
              <div className="vai-flex vai-items-center vai-justify-between vai-gap-2">
                <span
                  id={`goal-label-${goal.providerId}`}
                  className="vai-font-inter vai-text-sm vai-font-medium vai-leading-none vai-text-zinc-900"
                >
                  {goal.providerName}
                </span>
                <div className="vai-flex vai-h-9 vai-w-[142px] vai-shrink-0 vai-items-center vai-gap-2.5 vai-rounded-md vai-border vai-border-[#E4E4E7] vai-bg-white vai-px-3 focus-within:vai-border-[#2368F6] focus-within:vai-outline-none">
                  <span className="vai-text-sm vai-text-zinc-500" aria-hidden>
                    $
                  </span>
                  <Input
                    id={`goal-${goal.providerId}`}
                    type="text"
                    inputMode="numeric"
                    value={
                      goalValues[goal.providerId] ??
                      formatCurrencyDisplay(goal.goal)
                    }
                    onChange={(e) =>
                      handleGoalChange(goal.providerId, e.target.value)
                    }
                    onBlur={() => handleGoalBlur(goal.providerId)}
                    className="!vai-h-full vai-min-w-0 vai-flex-1 !vai-border-0 vai-bg-transparent vai-p-0 vai-text-right vai-text-sm !vai-shadow-none !vai-outline-none focus:!vai-ring-0 focus:!vai-ring-offset-0 focus-visible:!vai-ring-0 focus-visible:!vai-ring-offset-0"
                    data-testid={`daily-goal-input-${goal.providerId}`}
                    aria-labelledby={`goal-label-${goal.providerId}`}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        <DialogFooter className="vai-shrink-0 vai-flex vai-items-center vai-justify-between vai-gap-4 vai-pt-4">
          <div className="vai-flex vai-min-w-0 vai-flex-1 vai-justify-start vai-text-left">
            {lastUpdatedBy && (
              <p className="vai-text-[11px] vai-leading-tight vai-text-zinc-500">
                {lastUpdatedDate
                  ? t`Last Updated: ${lastUpdatedDate} by ${lastUpdatedBy}`
                  : t`Last Updated by ${lastUpdatedBy}`}
              </p>
            )}
          </div>
          <div className="vai-flex vai-shrink-0 vai-gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="vai-border-zinc-300 vai-bg-white hover:vai-bg-zinc-50"
              data-testid="daily-goal-modal-cancel"
            >
              {t`Cancel`}
            </Button>
            <Button
              type="button"
              onClick={handleSave}
              disabled={isLoading || !hasChanges}
              className="vai-bg-blue-600 hover:vai-bg-blue-700 vai-text-white"
              data-testid="daily-goal-modal-save"
            >
              {isLoading ? t`Saving...` : t`Save`}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
